import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/lf_logo.png";
import { consumePostLoginRedirect } from "../utils/postLoginRedirect";
import "./Login.css";

const Login = ({ setToken }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // Carica username salvato al mount
    useEffect(() => {
        const savedUsername = localStorage.getItem("rememberedUsername");
        if (savedUsername) {
            setUsername(savedUsername);
            setRememberMe(true);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const response = await axios.post(`${API_URL}/auth/login`, {
                username,
                password,
            });

            // Gestisci Remember Me
            if (rememberMe) {
                localStorage.setItem("rememberedUsername", username);
            } else {
                localStorage.removeItem("rememberedUsername");
            }

            setToken({
                token: response.data.token,
                role: response.data.role,
                username: response.data.username,
                permissions: response.data.permissions,
                allowedRoutes: response.data.allowedRoutes,
                tagMap: response.data.tagMap
            });

            // Torna alla pagina da cui l'utente era stato dirottato al login
            // (scorciatoia iOS, link condiviso); altrimenti home di default.
            // Se la rotta non è consentita per il ruolo, il catch-all in App.js
            // reindirizza comunque alla home corretta.
            const redirectTo = consumePostLoginRedirect() || "/";

            // Piccolo delay per mostrare l'animazione di successo
            setTimeout(() => {
                navigate(redirectTo, { replace: true });
            }, 300);
        } catch (err) {
            setLoading(false);
            if (err.response) {
                setError("Credenziali non valide. Riprova.");
            } else if (err.request) {
                setError("Impossibile connettersi al server.");
            } else {
                setError("Si è verificato un errore. Riprova.");
            }
        }
    };

    return (
        <div className="modern-login-container">
            {/* Left Side - Illustration */}
            <div className="login-illustration-side">
                <div className="illustration-content">
                    <div className="floating-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                        <div className="shape shape-3"></div>
                    </div>

                    {/* SVG Illustration */}
                    <svg className="login-illustration" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
                        {/* Background Circle */}
                        <circle cx="250" cy="250" r="200" fill="url(#gradient1)" opacity="0.1"/>

                        {/* Laptop */}
                        <g className="laptop-group">
                            <rect x="150" y="200" width="200" height="130" rx="5" fill="#2E3B55" />
                            <rect x="160" y="210" width="180" height="100" fill="#4A90E2" opacity="0.8"/>
                            <rect x="170" y="220" width="60" height="40" rx="3" fill="#fff" opacity="0.9"/>
                            <rect x="240" y="220" width="80" height="8" rx="2" fill="#fff" opacity="0.7"/>
                            <rect x="240" y="235" width="80" height="8" rx="2" fill="#fff" opacity="0.5"/>
                            <rect x="170" y="270" width="160" height="8" rx="2" fill="#C2D103" opacity="0.8"/>
                            <polygon points="130,330 370,330 350,345 150,345" fill="#1E2A3A"/>
                        </g>

                        {/* Person */}
                        <g className="person-group">
                            <circle cx="100" cy="150" r="25" fill="#4A90E2"/>
                            <path d="M 75 175 Q 75 200 100 210 Q 125 200 125 175 Z" fill="#2E3B55"/>
                        </g>

                        {/* Checkmarks */}
                        <g className="checkmark-group">
                            <circle cx="380" cy="120" r="15" fill="#C2D103" className="check-circle"/>
                            <path d="M 373 120 L 377 124 L 387 114" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        </g>

                        {/* Gradients */}
                        <defs>
                            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{stopColor: '#4A90E2', stopOpacity: 1}} />
                                <stop offset="100%" style={{stopColor: '#092E6E', stopOpacity: 1}} />
                            </linearGradient>
                        </defs>
                    </svg>

                    <div className="illustration-text">
                        <h2>Benvenuto su LF23 Dashboard</h2>
                        <p>Gestione produttività e monitoraggio in tempo reale</p>
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="login-form-side">
                <div className="login-form-container">
                    <div className="logo-section">
                        <img src={logo} alt="Logo LF" className="login-logo-modern" />
                    </div>

                    <div className="welcome-text">
                        <h1>Accedi</h1>
                        <p>Inserisci le tue credenziali per continuare</p>
                    </div>

                    <form onSubmit={handleSubmit} className="modern-login-form">
                        {/* Username Input */}
                        <div className="input-wrapper">
                            <label htmlFor="username" className="input-label">Username</label>
                            <div className="input-container">
                                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <input
                                    id="username"
                                    type="text"
                                    className="modern-input"
                                    placeholder="Inserisci il tuo username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="username"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="input-wrapper">
                            <label htmlFor="password" className="input-label">Password</label>
                            <div className="input-container">
                                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    className="modern-input"
                                    placeholder="Inserisci la tua password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex="-1"
                                >
                                    {showPassword ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Remember Me */}
                        <div className="form-options">
                            <label className="remember-me-label">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="remember-checkbox"
                                />
                                <span className="checkbox-custom"></span>
                                <span className="checkbox-text">Ricordami</span>
                            </label>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="error-container">
                                <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className={`modern-submit-btn ${loading ? 'loading' : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span>Accesso in corso...</span>
                                </>
                            ) : (
                                <>
                                    <span>Accedi</span>
                                    <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                        <polyline points="12 5 19 12 12 19"></polyline>
                                    </svg>
                                </>
                            )}
                        </button>
                    </form>

                    <div className="login-footer">
                        <p>&copy; {new Date().getFullYear()} LF23 Dashboard. Tutti i diritti riservati.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
